/* Pia › Einstellungen: „Sprachtest“ (misst die Erkennung mit festen Sätzen) und „Wörterbuch“ (lernt aus Korrekturen).
   Nur Texte: das Audio des Sprachtests wird nirgends gespeichert. */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Loader2, Trash2, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { AufnahmeLeiste, MikrofonKnopf, useSprachaufnahme } from '@/components/PiaMikrofon';

const fehler = (e, std) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : std);
const prozent = (w) => `${Math.round((w || 0) * 100)} %`;

function Unterschiede({ liste }) {
  if (!liste?.length) return <span className="text-green-700 dark:text-green-400">alles richtig</span>;
  return (
    <span className="space-x-2">
      {liste.map((u, i) => (
        <span key={i} className={u.art === 'zu_viel' ? 'text-amber-600' : 'text-destructive'}>
          {u.art === 'falsch' && `${u.erwartet} → ${u.erkannt}`}
          {u.art === 'fehlt' && `fehlt: ${u.erwartet}`}
          {u.art === 'zu_viel' && `zu viel: ${u.erkannt}`}
        </span>
      ))}
    </span>
  );
}

export function Sprachtest({ admin }) {
  const [d, setD] = useState(null);
  const [nr, setNr] = useState(1);
  const [letzte, setLetzte] = useState(null);
  const laden = useCallback(() => api.get('/assistenz/sprache/test').then((r) => setD(r.data)).catch((x) => toast.error(fehler(x, 'Sprachtest nicht geladen'))), []);
  useEffect(() => { laden(); }, [laden]);
  const s = useSprachaufnahme({
    pfad: `/assistenz/sprache/test/${nr}`,
    onText: (text, modell, daten) => { setLetzte(daten); laden(); },
  });
  if (!d) return <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const satz = d.saetze.find((x) => x.nr === nr) || d.saetze[0];
  const weiter = () => { setLetzte(null); setNr(nr >= d.saetze.length ? 1 : nr + 1); };
  const vorschlagen = async (w) => {
    try { await api.post('/assistenz/woerterbuch', { art: 'ersetzung', falsch: w.erkannt || w.erwartet, richtig: w.erwartet, status: 'vorschlag', quelle: 'sprachtest' }); toast.success('Als Vorschlag ins Wörterbuch'); }
    catch (x) { toast.error(fehler(x, 'Nicht gespeichert')); }
  };
  const ue = d.uebersicht;
  return (
    <Card><CardContent className="p-4 space-y-3" data-testid="pia-sprachtest">
      <h2 className="text-sm font-bold">Sprachtest</h2>
      <p className="text-sm text-muted-foreground">Lies den Satz laut vor. Pia erkennt ihn mit genau derselben Kette wie im Chat und zählt die Fehler. Gespeichert wird nur der Text, nie die Aufnahme.</p>
      <div className="rounded-md border border-input p-3 space-y-2">
        <div className="text-xs text-muted-foreground">Satz {satz.nr} von {d.saetze.length}</div>
        <p className="text-base font-medium" data-testid="pia-test-satz">{satz.text}</p>
        <div className="flex gap-2 items-center">
          {s.zustand !== 'ruhig' ? <AufnahmeLeiste s={s} /> : (<>
            <MikrofonKnopf onClick={s.start} />
            <Button size="sm" variant="outline" onClick={weiter}>Nächster Satz</Button>
          </>)}
        </div>
        {s.fehler && <p role="alert" className="text-xs text-destructive">{s.fehler}</p>}
        {letzte && (
          <div className="text-sm space-y-1" data-testid="pia-test-ergebnis">
            <div>Erkannt: „{letzte.erkannt}“</div>
            <div>Fehlerrate: <b>{prozent(letzte.wer)}</b> · <Unterschiede liste={letzte.unterschiede} /></div>
          </div>
        )}
      </div>
      {ue.je_modell.length > 0 && (
        <div className="space-y-1">
          <h3 className="text-xs font-bold uppercase text-muted-foreground">Ø Fehlerrate{d.alle ? ' (alle Nutzer)' : ''}</h3>
          {ue.je_modell.map((m, i) => (
            <div key={i} className="text-sm flex flex-wrap gap-x-3"><b>{prozent(m.wer_mittel)}</b><span>{m.modell}</span>
              <span className="text-muted-foreground">{m.anzahl} Sätze · {Object.entries(m.einstellungen).map(([k, v]) => `${k}=${String(v)}`).join(', ')}</span></div>
          ))}
        </div>
      )}
      {ue.falsche_woerter.length > 0 && (
        <div className="space-y-1">
          <h3 className="text-xs font-bold uppercase text-muted-foreground">Häufigste falsche Wörter</h3>
          {ue.falsche_woerter.map((w, i) => (
            <div key={i} className="text-sm flex items-center gap-2">
              <span>{w.erwartet} → {w.erkannt || '(fehlt)'} · {w.anzahl}×</span>
              {admin && w.erkannt && <Button size="sm" variant="ghost" onClick={() => vorschlagen(w)}>als Vorschlag</Button>}
            </div>
          ))}
        </div>
      )}
    </CardContent></Card>
  );
}

export function Woerterbuch() {
  const [d, setD] = useState(null);
  const [neu, setNeu] = useState({ art: 'ersetzung', falsch: '', richtig: '' });
  const laden = useCallback(() => api.get('/assistenz/woerterbuch').then((r) => setD(r.data)).catch((x) => toast.error(fehler(x, 'Wörterbuch nicht geladen'))), []);
  useEffect(() => { laden(); }, [laden]);
  if (!d) return <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const tu = async (fn, ok) => { try { await fn(); toast.success(ok); laden(); } catch (x) { toast.error(fehler(x, 'Nicht geklappt')); } };
  const anlegen = (e) => {
    e.preventDefault();
    tu(() => api.post('/assistenz/woerterbuch', { art: neu.art, richtig: neu.richtig, ...(neu.art === 'ersetzung' ? { falsch: neu.falsch } : {}) }), 'Eingetragen')
      .then(() => setNeu({ ...neu, falsch: '', richtig: '' }));
  };
  const status = (id, st) => tu(() => api.patch(`/assistenz/woerterbuch/${id}`, { status: st }), 'Gespeichert');
  const weg = (id) => tu(() => api.delete(`/assistenz/woerterbuch/${id}`), 'Gelöscht');
  return (
    <Card><CardContent className="p-4 space-y-3" data-testid="pia-woerterbuch">
      <h2 className="text-sm font-bold">Wörterbuch</h2>
      <p className="text-sm text-muted-foreground">Begriffe helfen der Erkennung, Ersetzungen korrigieren falsch erkannte Wörter. Wenn du erkannten Text vor dem Senden änderst, merkt sich Pia das als Vorschlag – aktiv wird nur, was die Leitung bestätigt.</p>
      {d.admin && (
        <form onSubmit={anlegen} className="flex flex-wrap gap-2 items-end">
          <select value={neu.art} onChange={(e) => setNeu({ ...neu, art: e.target.value })} className="h-9 rounded-md border border-input bg-background px-2 text-sm" aria-label="Art">
            <option value="ersetzung">Ersetzung</option><option value="begriff">Begriff</option>
          </select>
          {neu.art === 'ersetzung' && <input value={neu.falsch} onChange={(e) => setNeu({ ...neu, falsch: e.target.value })} placeholder="falsch erkannt" maxLength={80} className="h-9 rounded-md border border-input bg-background px-2 text-sm" />}
          <input value={neu.richtig} onChange={(e) => setNeu({ ...neu, richtig: e.target.value })} placeholder="richtig" maxLength={80} className="h-9 rounded-md border border-input bg-background px-2 text-sm" />
          <Button size="sm" type="submit" disabled={!neu.richtig.trim() || (neu.art === 'ersetzung' && !neu.falsch.trim())}>Eintragen</Button>
        </form>
      )}
      {d.eintraege.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Einträge.</p>}
      <div className="space-y-1">
        {d.eintraege.map((e) => (
          <div key={e.id} className="flex flex-wrap items-center gap-2 text-sm" data-testid="pia-wb-eintrag">
            <span className={e.status === 'abgelehnt' ? 'line-through text-muted-foreground' : ''}>{e.art === 'ersetzung' ? `${e.falsch} → ${e.richtig}` : e.richtig}</span>
            <span className="text-xs text-muted-foreground">{e.status === 'vorschlag' ? 'Vorschlag' : e.status === 'aktiv' ? 'aktiv' : 'abgelehnt'} · {e.quelle} · {e.anzahl}×</span>
            {d.admin && (
              <span className="ml-auto flex gap-1">
                {e.status !== 'aktiv' && <Button size="sm" variant="outline" onClick={() => status(e.id, 'aktiv')} aria-label="Bestätigen"><Check className="w-4 h-4" /></Button>}
                {e.status !== 'abgelehnt' && <Button size="sm" variant="outline" onClick={() => status(e.id, 'abgelehnt')} aria-label="Ablehnen"><X className="w-4 h-4" /></Button>}
                <Button size="sm" variant="ghost" onClick={() => weg(e.id)} aria-label="Löschen"><Trash2 className="w-4 h-4" /></Button>
              </span>
            )}
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}
